/**
 * ImportPreview - Composant d'affichage du preview des données à importer
 * Design System: Dashboard context (bg-white, text-gray-900)
 *
 * Inclut désormais un mode "Vue détaillée" avec:
 * - Pagination côté serveur pour les gros volumes
 * - Filtres par statut et recherche
 * - Correction inline des erreurs
 * - Export CSV des erreurs
 */

import { useMemo, useState } from 'react';
import { Table, AlertTriangle, CheckCircle, Info, ChevronDown, ChevronUp, LayoutGrid, List } from 'lucide-react';
import type { ImportPreview as ImportPreviewType, ImportConfig } from '../types/import.types';
import { ImportLinesDataTable } from './ImportLinesDataTable';

// Champs FEC standard avec labels lisibles
const FEC_FIELDS = [
  { value: 'JournalCode', label: 'Code Journal' },
  { value: 'JournalLib', label: 'Libellé Journal' },
  { value: 'EcritureNum', label: 'N° Écriture' },
  { value: 'EcritureDate', label: 'Date Écriture' },
  { value: 'CompteNum', label: 'N° Compte' },
  { value: 'CompteLib', label: 'Libellé Compte' },
  { value: 'CompAuxNum', label: 'N° Compte Auxiliaire' },
  { value: 'CompAuxLib', label: 'Libellé Compte Auxiliaire' },
  { value: 'PieceRef', label: 'Référence Pièce' },
  { value: 'PieceDate', label: 'Date Pièce' },
  { value: 'EcritureLib', label: 'Libellé Écriture' },
  { value: 'Debit', label: 'Débit' },
  { value: 'Credit', label: 'Crédit' },
  { value: 'EcritureLet', label: 'Lettrage' },
  { value: 'DateLet', label: 'Date Lettrage' },
  { value: 'ValidDate', label: 'Date Validation' },
  { value: 'Montantdevise', label: 'Montant Devise' },
  { value: 'Idevise', label: 'Code Devise' },
];

interface ImportPreviewProps {
  preview: ImportPreviewType;
  config: ImportConfig;
  onConfigChange: (config: ImportConfig) => void;
  importId?: string; // Requis pour la vue détaillée avec pagination
}

type ViewMode = 'simple' | 'detailed';

export function ImportPreview({ preview, config, onConfigChange, importId }: ImportPreviewProps) {
  const [showAllColumns, setShowAllColumns] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('simple');

  // Toutes les colonnes disponibles
  const allColumns = useMemo(() => preview.columns || [], [preview.columns]);

  // Colonnes pour le tableau de preview (limité à 10 pour lisibilité)
  const previewTableColumns = useMemo(() => {
    return allColumns.slice(0, 10);
  }, [allColumns]);

  // Auto-mapping intelligent: si la colonne source correspond à un champ FEC, mapper automatiquement
  const effectiveMapping = useMemo(() => {
    const baseMapping = config.columnMapping || preview.suggestedMapping || {};
    const autoMapping: Record<string, string> = {};

    // Auto-mapper les colonnes qui correspondent exactement aux champs FEC
    allColumns.forEach(col => {
      const fecField = FEC_FIELDS.find(f =>
        f.value.toLowerCase() === col.toLowerCase() ||
        f.value === col
      );
      if (fecField && !baseMapping[col]) {
        autoMapping[col] = fecField.value;
      }
    });

    return { ...autoMapping, ...baseMapping };
  }, [config.columnMapping, preview.suggestedMapping, allColumns]);

  // Si preview n'a pas de données, afficher un message
  if (!preview.columns || preview.columns.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
        <Info className="w-5 h-5 text-gray-400 mr-2" />
        <span className="text-gray-500">Aucune donnée à prévisualiser</span>
      </div>
    );
  }

  const handleMappingChange = (sourceColumn: string, targetField: string) => {
    const newMapping = { ...effectiveMapping };
    if (targetField) {
      newMapping[sourceColumn] = targetField;
    } else {
      delete newMapping[sourceColumn];
    }
    onConfigChange({
      ...config,
      columnMapping: newMapping,
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">Lignes détectées</div>
          <div className="text-2xl font-semibold text-gray-900">{preview.totalRows}</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">Colonnes</div>
          <div className="text-2xl font-semibold text-gray-900">{preview.columns.length}</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">Format détecté</div>
          <div className="text-lg font-semibold text-gray-900">{preview.detectedFormat}</div>
        </div>
        {preview.stats && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Écritures comptables</div>
            <div className="text-2xl font-semibold text-gray-900">{preview.stats.entriesCount}</div>
          </div>
        )}
      </div>

      {/* FEC-specific stats */}
      {preview.stats && (
        <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Info className="w-5 h-5 text-teal-600" />
            Statistiques FEC
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Comptes distincts:</span>{' '}
              <span className="font-medium text-gray-900">{preview.stats.distinctAccounts}</span>
            </div>
            <div>
              <span className="text-gray-500">Journaux distincts:</span>{' '}
              <span className="font-medium text-gray-900">{preview.stats.distinctJournals}</span>
            </div>
            <div>
              <span className="text-gray-500">Écritures équilibrées:</span>{' '}
              <span className="font-medium text-gray-900">
                {preview.stats.balancedEntries}/{preview.stats.entriesCount}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Période:</span>{' '}
              <span className="font-medium text-gray-900">
                {preview.stats.periodStart && preview.stats.periodEnd
                  ? `${new Date(preview.stats.periodStart).toLocaleDateString('fr-FR')} - ${new Date(preview.stats.periodEnd).toLocaleDateString('fr-FR')}`
                  : 'N/A'}
              </span>
            </div>
          </div>
          {preview.stats.unbalancedEntries > 0 && (
            <div className="mt-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">
                {preview.stats.unbalancedEntries} écriture(s) non équilibrée(s) détectée(s)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Column Mapping */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Table className="w-5 h-5 text-gray-500" />
            Mapping des colonnes ({allColumns.length})
          </h4>
          <button
            onClick={() => setShowAllColumns(!showAllColumns)}
            className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
          >
            {showAllColumns ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Réduire
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Voir tout ({allColumns.length})
              </>
            )}
          </button>
        </div>

        {/* Résumé du mapping */}
        <div className="mb-3 p-3 bg-teal-50 rounded-lg text-sm">
          <span className="text-teal-700">
            <CheckCircle className="w-4 h-4 inline mr-1" />
            {Object.keys(effectiveMapping).filter(k => effectiveMapping[k]).length} / {allColumns.length} colonnes mappées automatiquement
          </span>
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-gray-700 font-medium">Colonne source</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium">→</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium">Champ cible FEC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(showAllColumns ? allColumns : allColumns.slice(0, 8)).map((column) => {
                const mappedTo = effectiveMapping[column];
                const mappedField = FEC_FIELDS.find(f => f.value === mappedTo);
                return (
                  <tr key={column} className={mappedTo ? 'bg-green-50/50' : 'bg-amber-50/50'}>
                    <td className="px-4 py-3 text-gray-900 font-mono text-xs">{column}</td>
                    <td className="px-4 py-3 text-gray-400">→</td>
                    <td className="px-4 py-3">
                      <select
                        value={mappedTo || ''}
                        onChange={(e) => handleMappingChange(column, e.target.value)}
                        className={`w-full px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                          mappedTo
                            ? 'border-green-300 text-green-700'
                            : 'border-amber-300 text-amber-700'
                        }`}
                      >
                        <option value="">— Ignorer cette colonne —</option>
                        <optgroup label="Champs FEC obligatoires">
                          {FEC_FIELDS.slice(0, 13).map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Champs FEC optionnels">
                          {FEC_FIELDS.slice(13).map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      {mappedTo && mappedField && (
                        <span className="text-xs text-green-600 mt-1 block">
                          ✓ {mappedField.label}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Preview avec toggle Vue simple / Vue détaillée */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Aperçu des données</h4>
          {importId && preview.totalRows > 10 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Vue:</span>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setViewMode('simple')}
                  className={`px-3 py-1.5 text-sm flex items-center gap-1 ${
                    viewMode === 'simple'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  Simple
                </button>
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`px-3 py-1.5 text-sm flex items-center gap-1 ${
                    viewMode === 'detailed'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <List className="w-4 h-4" />
                  Détaillée
                </button>
              </div>
            </div>
          )}
        </div>

        {viewMode === 'detailed' && importId ? (
          // Vue détaillée avec DataTable enterprise
          <ImportLinesDataTable
            importId={importId}
            columns={allColumns}
          />
        ) : (
          // Vue simple (aperçu rapide)
          <>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-700 font-medium">#</th>
                    {previewTableColumns.map((col: string) => (
                      <th key={col} className="px-3 py-2 text-left text-gray-700 font-medium truncate max-w-[120px]">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {preview.preview.slice(0, 10).map((row) => (
                    <tr key={row.rowNumber} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                      {previewTableColumns.map((col: string) => (
                        <td key={col} className="px-3 py-2 text-gray-900 truncate max-w-[120px]" title={String(row.data[col] ?? '')}>
                          {row.data[col] ?? '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                Affichage des 10 premières lignes sur {preview.totalRows.toLocaleString('fr-FR')} • {allColumns.length > 10 && `${allColumns.length - 10} colonnes masquées`}
              </p>
              {importId && preview.totalRows > 10 && (
                <button
                  onClick={() => setViewMode('detailed')}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Voir toutes les {preview.totalRows.toLocaleString('fr-FR')} lignes →
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
